import React from 'react';
import { Box, SelectList, Option } from '@indeed/ifl-components';

interface DateSelectProps {
    /** Date value in partial ISO format (e.g., '2023', '2023-05', '2023-05-15') */
    value?: string;
    /** Called when date changes, emits partial ISO format immediately */
    onChange: (value: string) => void;
    /** Whether the field is disabled */
    disabled?: boolean;
}

const DateSelect: React.FC<DateSelectProps> = ({ value = '', onChange, disabled = false }) => {
    // Parse partial ISO date format into separate components for dropdowns
    // Supports: '2023', '2023-05', '2023-05-15'
    const parseDateValue = (dateStr: string) => {
        if (!dateStr) return { year: '', month: '', day: '' };
        const parts = dateStr.split('-');
        return {
            year: parts[0] || '',
            month: parts[1] ? parseInt(parts[1], 10).toString() : '', // Remove leading zeros: "05" → "5"
            day: parts[2] ? parseInt(parts[2], 10).toString() : '', // Remove leading zeros: "08" → "8"
        };
    };

    // Create partial ISO date strings (like jspl getISODate)
    const getISODate = (year: string, month: string, day: string): string => {
        const yearNumber = Number(year);

        if (!yearNumber) {
            return '';
        }

        const parts = [String(yearNumber)];

        const monthNumber = Number(month);

        if (monthNumber >= 1 && monthNumber <= 12) {
            parts.push(String(monthNumber).padStart(2, '0'));

            const dayNumber = Number(day);

            if (dayNumber >= 1 && dayNumber <= 31) {
                parts.push(String(dayNumber).padStart(2, '0'));
            }
        }

        const isoDate = parts.filter(Boolean).join('-');

        // Validate the partial date is real
        let realDate = '';
        try {
            realDate = new Date(isoDate).toISOString();
        } catch {
            // fail silently
        }

        if (!realDate.startsWith(isoDate)) {
            return '';
        }

        return isoDate;
    };

    const dateComponents = parseDateValue(value);

    // Generate dropdown option arrays
    const yearOptions = Array.from({ length: 80 }, (_, i) => {
        const year = new Date().getFullYear() - 16 - i; // Start from eligible age
        return year.toString();
    });

    const monthOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
    const dayOptions = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

    // Handle SelectList changes (IFL SelectList uses onSelectionChange with string array)
    const handleDateChange = (field: 'year' | 'month' | 'day') => (selectedValues: string[]) => {
        const newValue = selectedValues[0] ?? '';

        const newComponents = {
            ...dateComponents,
            [field]: newValue,
        };

        // ✅ JSPL PATTERN: Emit onChange immediately with partial ISO dates
        // Supports: '2023', '2023-05', '2023-05-15'
        const partialISODate = getISODate(
            newComponents.year,
            newComponents.month,
            newComponents.day,
        );

        // Always call onChange - empty string when no valid partial date
        onChange(partialISODate);
    };

    return (
        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
                <SelectList
                    selectedValues={dateComponents.year ? [dateComponents.year] : []}
                    onSelectionChange={handleDateChange('year')}
                    disabled={disabled}
                    placeholder="選択"
                >
                    <Option value="">選択</Option>
                    {yearOptions.map((year) => (
                        <Option key={year} value={year}>
                            {year}
                        </Option>
                    ))}
                </SelectList>
            </Box>

            <Box sx={{ flex: 1 }}>
                <SelectList
                    selectedValues={dateComponents.month ? [dateComponents.month] : []}
                    onSelectionChange={handleDateChange('month')}
                    disabled={disabled}
                    placeholder="選択"
                >
                    <Option value="">選択</Option>
                    {monthOptions.map((month) => (
                        <Option key={month} value={month}>
                            {month}
                        </Option>
                    ))}
                </SelectList>
            </Box>

            <Box sx={{ flex: 1 }}>
                <SelectList
                    selectedValues={dateComponents.day ? [dateComponents.day] : []}
                    onSelectionChange={handleDateChange('day')}
                    disabled={disabled}
                    placeholder="選択"
                >
                    <Option value="">選択</Option>
                    {dayOptions.map((day) => (
                        <Option key={day} value={day}>
                            {day}
                        </Option>
                    ))}
                </SelectList>
            </Box>
        </Box>
    );
};

export default DateSelect;
